/**
 * 型安全なCSS文字列組み合わせユーティリティ
 * FL-48で頻出するクラス組み合わせを関数化
 */

// 基本的なスタイル型定義
export type ButtonVariant = 'primary' | 'secondary' | 'danger' | 'ghost';
export type ButtonSize = 'sm' | 'md' | 'lg';
export type TouchTargetSize = '44' | '48' | '56';

/**
 * 複数のクラス名を結合し、falsy値を除外
 */
export function cn(...classes: (string | undefined | null | false)[]): string {
  return classes.filter(Boolean).join(' ');
}

/**
 * classNames関数のエイリアス（下位互換性のため）
 */
export const classNames = cn;

/**
 * タッチターゲット基本クラス
 */
export function touchTarget(size: TouchTargetSize = '44'): string {
  return cn(
    `min-h-[${size}px]`,
    size !== '44' ? `min-w-[${size}px]` : 'min-w-[44px]',
    'touch-target',
    'focus:outline-none focus:ring-2 focus:ring-offset-2'
  );
}

// buttonStyles function removed - use Button component instead
// Button component now provides unified styling with Tailwind + CSS fallback

/**
 * アイコンボタンスタイル生成
 */
export function iconButtonStyles(
  variant: ButtonVariant = 'ghost',
  size: TouchTargetSize = '44',
  className?: string
): string {
  const baseClasses = cn(
    'flex items-center justify-center rounded-lg transition-colors',
    touchTarget(size),
    'button-touch button-ripple'
  );

  const variantClasses = {
    primary: 'bg-primary-600 text-white hover:bg-primary-700 focus:ring-primary-500',
    secondary:
      'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 focus:ring-gray-500',
    danger:
      'text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-200 hover:bg-red-50 dark:hover:bg-red-900/20 focus:ring-red-500',
    ghost:
      'text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 focus:ring-gray-500',
  };

  return cn(baseClasses, variantClasses[variant], className);
}

/**
 * カードスタイル生成
 */
export function cardStyles(interactive: boolean = false, className?: string): string {
  const baseClasses = 'bg-white dark:bg-gray-800 rounded-lg shadow-md';

  if (interactive) {
    return cn(baseClasses, 'card-hover fade-in cursor-pointer', touchTarget(), className);
  }

  return cn(baseClasses, 'fade-in', className);
}

/**
 * ナビゲーションアイテムスタイル生成
 */
export function navItemStyles(isActive: boolean = false, className?: string): string {
  const baseClasses = cn(
    'flex items-center px-4 py-2.5 text-sm font-medium rounded-lg transition-colors',
    touchTarget(),
    'focus:ring-primary-500 focus:ring-offset-2 focus:ring-offset-white dark:focus:ring-offset-gray-800'
  );

  const activeClasses = isActive
    ? 'bg-primary-600 text-white'
    : 'text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 focus:bg-gray-200 dark:focus:bg-gray-700';

  return cn(baseClasses, activeClasses, className);
}

/**
 * フォーム入力スタイル生成
 */
export function inputStyles(hasError: boolean = false, className?: string): string {
  const baseClasses = cn(
    'w-full px-4 py-3 border rounded-lg',
    'bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white',
    'focus:ring-primary-500 focus:border-primary-500',
    touchTarget(),
    'transition-colors'
  );

  const stateClasses = hasError
    ? 'border-red-300 dark:border-red-600 focus:ring-red-500 focus:border-red-500'
    : 'border-gray-300 dark:border-gray-600';

  return cn(baseClasses, stateClasses, className);
}

/**
 * モーダルオーバーレイスタイル
 */
export function overlayStyles(className?: string): string {
  return cn(
    'fixed inset-0 bg-gray-900/20 dark:bg-black/30 z-40',
    'transition-opacity modal-overlay',
    className
  );
}

// オーバーレイ色定数（Tailwindクラスとの対応）
export const OVERLAY_COLORS = {
  light: 'rgba(55, 65, 81, 0.2)', // bg-gray-900/20 相当
  dark: 'rgba(0, 0, 0, 0.3)', // bg-black/30 相当
} as const;

/**
 * オーバーレイ用のインラインスタイル（フォールバック）
 */
export function overlayInlineStyles(): React.CSSProperties {
  return {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'var(--overlay-bg, rgba(55, 65, 81, 0.2))', // bg-gray-900/20 相当
    zIndex: 40,
    transition: 'opacity 150ms ease',
  };
}

/**
 * ダークモード用オーバーレイのインラインスタイル（フォールバック）
 */
export function overlayInlineStylesDark(): React.CSSProperties {
  return {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'var(--overlay-bg-dark, rgba(0, 0, 0, 0.3))', // bg-black/30 相当
    zIndex: 40,
    transition: 'opacity 150ms ease',
  };
}

/**
 * レスポンシブグリッドスタイル生成
 */
export function responsiveGridStyles(
  cols: { mobile?: number; tablet?: number; desktop?: number } = {
    mobile: 1,
    tablet: 2,
    desktop: 3,
  },
  gap: string = 'gap-4 sm:gap-6',
  className?: string
): string {
  const { mobile = 1, tablet = 2, desktop = 3 } = cols;

  return cn(
    'grid',
    `grid-cols-${mobile}`,
    tablet ? `sm:grid-cols-${tablet}` : false,
    desktop ? `lg:grid-cols-${desktop}` : false,
    gap,
    className
  );
}
